/**
 * Experiment + ExperimentVariant schemas (issue #25, spec §16).
 *
 * Shared between:
 *   - `api/src/experiments/*` — request validation for the
 *     `/api/admin/sites/:siteId/experiments`,
 *     `/api/admin/experiments/:id`, and
 *     `/api/admin/variants/:id` endpoints,
 *   - `web/src/...` — TanStack Query hooks + the admin experiments list /
 *     detail pages use these types so the SPA + API agree on field names.
 *
 * Lifecycle (spec §16 Experiment Lifecycle Rules):
 *   - DRAFT → RUNNING → COMPLETED
 *   - PAUSED + ARCHIVED are reserved by the enum but not driven by this
 *     CRUD slice; subsequent issues (#27 metrics, future archival) own
 *     them.
 *
 * Variant target (spec §16 How Variants Reference Content):
 *   - `pageVersionId` — full-page variant (renderer swaps the entire
 *     PageVersion).
 *   - `contentOverridesJson` — block-level variant (renderer swaps
 *     specific ContentItem → ContentVersion mappings, leaving the rest of
 *     the page rendering from its currently-published versions).
 *   - Exactly one of these must be present per variant. The Zod schema
 *     enforces this with `.refine`; the API service additionally checks
 *     the cross-resource invariants (PageVersion exists + belongs to the
 *     experiment's site; ContentVersion ids resolve when applied later by
 *     issue #26's renderer).
 *
 * `contentOverridesJsonV1`:
 *   - The spec shows the column carrying a JSON ARRAY of
 *     `{ contentItemId, contentVersionId }`. Per §20 (Schema Versioning),
 *     every product-level JSON field must include a top-level
 *     `schemaVersion` integer, so we wrap the array in an object that
 *     carries the version. Read-time adapters can lift older shapes
 *     forward in the future.
 *   - At MVP scale we cap the override list at 50 entries — generous for
 *     the realistic block-count of a single page (the BlocksOrder cap is
 *     100), narrow enough to keep the JSONB column compact and the
 *     variant resolution loop bounded.
 */

import { z } from "zod";
import { analyticsEventTypeSchema } from "./analytics.js";
import type { AnalyticsEventType, ExperimentStatus, ExperimentTargetKind } from "../types/index.js";

/**
 * Mirror of Prisma's `ExperimentStatus` enum. Re-declared as a runtime
 * tuple so the Zod enum + the SPA's status chip both iterate it.
 */
export const EXPERIMENT_STATUSES = ["DRAFT", "RUNNING", "PAUSED", "COMPLETED", "ARCHIVED"] as const;
export const experimentStatusSchema = z.enum(
  EXPERIMENT_STATUSES,
) satisfies z.ZodType<ExperimentStatus>;

/**
 * Issue #247: mirror of Prisma's `ExperimentTargetKind` enum. Re-declared
 * here as a runtime tuple so the Zod enum + the SPA's create-experiment
 * radio iterate the same set.
 */
export const EXPERIMENT_TARGET_KINDS = ["PAGE_VERSION", "CONTENT_OVERRIDES"] as const;
export const experimentTargetKindSchema = z.enum(
  EXPERIMENT_TARGET_KINDS,
) satisfies z.ZodType<ExperimentTargetKind>;

/**
 * `contentOverridesJsonV1` — Zod-validated shape for
 * `ExperimentVariant.contentOverridesJson`. Wraps the spec's "array of
 * `{ contentItemId, contentVersionId }`" in a versioned envelope per
 * §20 Schema Versioning.
 *
 * `.strict()` rejects unknown top-level keys so a typo (e.g. `entries` /
 * `items`) surfaces as a 422 instead of being silently dropped.
 *
 * Implementation note: a future schemaVersion 2 would land a new schema
 * (e.g. `contentOverridesJsonV2`) and an adapter; never widen this
 * schema.
 */
export const contentOverridesJsonV1 = z
  .object({
    schemaVersion: z.literal(1),
    overrides: z
      .array(
        z
          .object({
            contentItemId: z.string().min(1).max(64),
            contentVersionId: z.string().min(1).max(64),
          })
          .strict(),
      )
      .min(0)
      .max(50),
  })
  .strict();
export type ContentOverridesJsonV1 = z.infer<typeof contentOverridesJsonV1>;

/**
 * Body schema for `POST /api/admin/sites/:siteId/experiments`.
 *
 * `pageId` is optional at the schema layer — but the API additionally
 * enforces that PAGE_VERSION experiments require a `pageId`
 * (`PAGE_ID_REQUIRED_FOR_PAGE_VERSION_EXPERIMENT`, issue #247). The Zod
 * layer enforces field shape; cross-resource invariants live in the
 * service. When `pageId` is supplied the service validates that it
 * belongs to `siteId` (`PAGE_NOT_FOUND`).
 *
 * `targetKind` is required (issue #247) — every experiment must declare
 * whether its variants swap whole pages or layer block-level overrides.
 * The field is immutable after creation (the PATCH schema does not
 * accept it).
 */
export const createExperimentSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    targetKind: experimentTargetKindSchema,
    pageId: z.string().min(1).max(64).optional(),
    goalEvent: analyticsEventTypeSchema.optional(),
    /**
     * Optional ISO datetime strings. Coerced to Date at the boundary so
     * the service can persist the values directly. The API enforces no
     * ordering here at MVP — the operator can intend an `endsAt` before
     * `startsAt` while drafting; the start endpoint owns the runtime
     * lifecycle gate.
     */
    startsAt: z.coerce.date().optional(),
    endsAt: z.coerce.date().optional(),
  })
  .strict();
export type CreateExperimentInput = z.infer<typeof createExperimentSchema>;

/**
 * Body schema for `PATCH /api/admin/experiments/:id`. Allowed only when
 * status=DRAFT (the service enforces this with 422 `EXPERIMENT_NOT_DRAFT`).
 *
 * `pageId` is intentionally NOT mutable — the experiment's scope is fixed
 * at creation. Variant targets carry their own page-version ids.
 *
 * Issue #247: `targetKind` is also immutable after creation. We
 * intentionally accept it at the schema layer so the service can surface
 * a precise 422 `ILLEGAL_FIELD_UPDATE` (rather than a generic
 * `VALIDATION_ERROR` from `.strict()`). The service rejects the field
 * before applying any other patch fields.
 */
export const updateExperimentSchema = z
  .object({
    name: z.string().trim().min(1).max(120).optional(),
    /**
     * Issue #247: forbidden after creation. Surfaces as
     * `ILLEGAL_FIELD_UPDATE` from the service layer when present.
     */
    targetKind: experimentTargetKindSchema.optional(),
    goalEvent: analyticsEventTypeSchema.optional(),
    startsAt: z.coerce.date().optional(),
    endsAt: z.coerce.date().optional(),
  })
  .strict()
  .refine((obj) => Object.keys(obj).length > 0, {
    message: "Update must include at least one field",
  });
export type UpdateExperimentInput = z.infer<typeof updateExperimentSchema>;

/**
 * Query-parameter schema for `GET /api/admin/sites/:siteId/experiments`.
 * Standard offset pagination per `standards/api-patterns.md` (default 20,
 * max 100). Optional `status` filter narrows the list to a single
 * lifecycle state.
 *
 * Issue #253 — `includeArchived` (default `false`) toggles whether
 * ARCHIVED rows are returned. Behaviour change from the previous slice:
 * the default list response NO LONGER includes ARCHIVED. Callers that
 * want every status (including ARCHIVED) must set
 * `includeArchived=true`. When the caller passes an explicit `status`
 * filter, `includeArchived` is irrelevant — the explicit filter wins.
 *
 * Coerced via Zod's boolean(`coerce: true`)-like pattern so query strings
 * (`?includeArchived=true`) are accepted alongside booleans. We don't
 * use `z.coerce.boolean()` directly because it would treat the string
 * `"false"` as truthy (any non-empty string is truthy in JS). The
 * `z.enum` + `.transform` pair below maps the two intended values
 * explicitly.
 */
export const listExperimentsQuerySchema = z
  .object({
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
    status: experimentStatusSchema.optional(),
    includeArchived: z
      .union([z.boolean(), z.enum(["true", "false"])])
      .optional()
      .transform((v) => v === true || v === "true"),
  })
  .strict();
export type ListExperimentsQuery = z.infer<typeof listExperimentsQuerySchema>;

/**
 * Body schema for `POST /api/admin/experiments/:id/variants`.
 *
 * Exactly one of `pageVersionId` / `contentOverridesJson` must be
 * present. The `.refine` clauses surface that as a Zod-level error so
 * the API can map them to precise codes (`VARIANT_TARGET_REQUIRED` /
 * `VARIANT_TARGET_AMBIGUOUS`) before reaching the service.
 *
 * Weight is a positive integer in `[1, 100]` — the service additionally
 * validates that `sum(variants.weight) === 100` at experiment-start time
 * (422 `WEIGHTS_DO_NOT_SUM_TO_100`).
 */
export const createVariantSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    weight: z.coerce.number().int().min(1).max(100),
    pageVersionId: z.string().min(1).max(64).optional(),
    contentOverridesJson: contentOverridesJsonV1.optional(),
  })
  .strict()
  .refine((obj) => obj.pageVersionId !== undefined || obj.contentOverridesJson !== undefined, {
    message: "Variant must include either pageVersionId or contentOverridesJson.",
    path: ["pageVersionId"],
  })
  .refine((obj) => !(obj.pageVersionId !== undefined && obj.contentOverridesJson !== undefined), {
    message: "Variant cannot include both pageVersionId and contentOverridesJson.",
    path: ["pageVersionId"],
  });
export type CreateVariantInput = z.infer<typeof createVariantSchema>;

/**
 * Body schema for `PATCH /api/admin/variants/:id`. Same single-target
 * rule as create: the resulting variant (after merge) must carry exactly
 * one of `pageVersionId` / `contentOverridesJson`.
 *
 * The "exactly one after merge" check needs the persisted row, so the
 * service performs it (with the precise codes) — at the schema layer we
 * only reject a single PATCH that supplies both fields at once.
 */
export const updateVariantSchema = z
  .object({
    name: z.string().trim().min(1).max(120).optional(),
    weight: z.coerce.number().int().min(1).max(100).optional(),
    pageVersionId: z.string().min(1).max(64).optional(),
    contentOverridesJson: contentOverridesJsonV1.optional(),
  })
  .strict()
  .refine((obj) => Object.keys(obj).length > 0, {
    message: "Update must include at least one field",
  })
  .refine((obj) => !(obj.pageVersionId !== undefined && obj.contentOverridesJson !== undefined), {
    message: "Variant cannot include both pageVersionId and contentOverridesJson.",
    path: ["pageVersionId"],
  });
export type UpdateVariantInput = z.infer<typeof updateVariantSchema>;

/**
 * Public-facing Experiment shape (list endpoint). ISO-string timestamps
 * for transport. Variant count + assignment count are surfaced as light
 * stats so the list view can render summary metadata without N+1 joins.
 *
 * `assignmentCount` is hard-coded to 0 in this slice; full metrics land
 * in #27 (Dev B's next round). The field is on the wire today so the
 * SPA can render the column from day one and #27 fills it in.
 */
export type ExperimentResponse = {
  id: string;
  siteId: string;
  pageId: string | null;
  name: string;
  status: ExperimentStatus;
  /**
   * Issue #247 — set at create-time, immutable thereafter. The UI uses
   * this to drive the add-variant editor (only the relevant target
   * picker shows) and to surface a "Whole page" vs. "Block-level
   * overrides" chip in the detail header.
   */
  targetKind: ExperimentTargetKind;
  goalEvent: AnalyticsEventType | null;
  startsAt: string | null;
  endsAt: string | null;
  createdAt: string;
  updatedAt: string;
  variantCount: number;
  assignmentCount: number;
};

/**
 * Public-facing Variant shape. The JSON field is kept opaque at the
 * boundary (`unknown`); clients narrow it with `contentOverridesJsonV1`
 * if they need to read its inner shape.
 */
export type ExperimentVariantResponse = {
  id: string;
  experimentId: string;
  name: string;
  weight: number;
  pageVersionId: string | null;
  contentOverridesJson: unknown;
  createdAt: string;
};

/**
 * Composite shape returned by `GET /api/admin/experiments/:id`. Includes
 * the experiment row itself and the full variants list. The renderer-side
 * lookup contract (issue #26) consumes the same shape — `variants[]` is
 * the canonical input to the public-side variant resolver.
 *
 * Renderer contract (consumed by issue #26):
 *   - For a public render of a Page, look up RUNNING experiments scoped
 *     to either (a) the same Page (`Experiment.pageId === page.id`) or
 *     (b) site-wide (`Experiment.pageId === null`) on the Page's Site.
 *   - For each, resolve the visitor's variant via
 *     `ExperimentAssignment` (sticky) or by weighted random + INSERT
 *     (first-touch).
 *   - Apply the variant: `pageVersionId` swaps the rendered PageVersion;
 *     `contentOverridesJson.overrides[]` swaps individual ContentItem →
 *     ContentVersion mappings. The control variant (no override) renders
 *     the page's currently-published versions.
 *   - Issue #26 owns this resolution + the assignment write.
 */
export type ExperimentDetailResponse = {
  experiment: ExperimentResponse;
  variants: ExperimentVariantResponse[];
};

/**
 * Per-variant slice of the `GET /api/admin/experiments/:id/metrics`
 * response (issue #27). Counts come from `ExperimentAssignment` (sticky
 * assignments, written by the renderer-side resolver in #149) and
 * `AnalyticsEvent` (PAGE_VIEW + the experiment's optional
 * `goalEvent`).
 *
 * `dailyAssignments` is a 7-entry array of last-7-days sparkline points
 * (UTC days, oldest first). The frontend draws a small bar/line chart
 * from it without a separate fetch. Empty array when assignments are
 * zero or the experiment is DRAFT.
 */
export type ExperimentVariantMetrics = {
  variantId: string;
  name: string;
  weight: number;
  assignments: number;
  goalEventCount: number;
  /** 0..1; 0 when assignments==0. */
  conversionRate: number;
  pageViews: number;
  /** Per-day count for the last 7 days, oldest → newest. */
  dailyAssignments: { date: string; count: number }[];
};

/**
 * Top-level shape returned by `GET /api/admin/experiments/:id/metrics`.
 * Goal-event counts default to 0 when `goalEvent` is null on the
 * experiment row.
 *
 * The endpoint is admin-only and a pure READ — no audit-log entry is
 * written.
 */
export type ExperimentMetricsResponse = {
  experimentId: string;
  status: ExperimentStatus;
  goalEvent: AnalyticsEventType | null;
  /** ISO datetime — `experiment.startsAt` or `experiment.createdAt`. */
  periodStart: string;
  /** ISO datetime — `experiment.endsAt` or `now()`. */
  periodEnd: string;
  variants: ExperimentVariantMetrics[];
  totals: {
    assignments: number;
    goalEventCount: number;
    /** 0..1; 0 when assignments==0. */
    conversionRate: number;
  };
};
